import { cn } from '@/lib/utils';
import React from 'react';
import { FileText, Mic, BarChart, Settings, PlayCircle, Award } from 'lucide-react';

type FeatureType = {
    title: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    description: string;
};

type FeatureCardPorps = React.ComponentProps<'div'> & {
    feature: FeatureType;
};

export function FeatureCard({ feature, className, ...props }: FeatureCardPorps) {
    const [p, setP] = React.useState<number[][]>([]);

    React.useEffect(() => {
        setP(genRandomPattern());
    }, []);

    return (
        <div className={cn('relative overflow-hidden p-6', className)} {...props}>
            <div className="pointer-events-none absolute top-0 left-1/2 -mt-2 -ml-20 h-full w-full [mask-image:linear-gradient(white,transparent)]">
                <div className="from-foreground/5 to-foreground/1 absolute inset-0 bg-gradient-to-r [mask-image:radial-gradient(farthest-side_at_top,white,transparent)] opacity-100">
                    <GridPattern
                        width={20}
                        height={20}
                        x="-12"
                        y="4"
                        squares={p}
                        className="fill-foreground/5 stroke-foreground/10 absolute inset-0 h-full w-full mix-blend-overlay"
                    />
                </div>
            </div>
            <feature.icon className="text-foreground/75 size-6" strokeWidth={1} aria-hidden />
            <h3 className="mt-10 text-sm md:text-base font-semibold">{feature.title}</h3>
            <p className="text-muted-foreground relative z-20 mt-2 text-xs font-light tracking-wide">{feature.description}</p>
        </div>
    );
}

function GridPattern({
    width,
    height,
    x,
    y,
    squares,
    ...props
}: React.ComponentProps<'svg'> & { width: number; height: number; x: string; y: string; squares?: number[][] }) {
    const patternId = React.useId();

    return (
        <svg aria-hidden="true" {...props}>
            <defs>
                <pattern id={patternId} width={width} height={height} patternUnits="userSpaceOnUse" x={x} y={y}>
                    <path d={`M.5 ${height}V.5H${width}`} fill="none" />
                </pattern>
            </defs>
            <rect width="100%" height="100%" strokeWidth={0} fill={`url(#${patternId})`} />
            {squares && (
                <svg x={x} y={y} className="overflow-visible">
                    {squares.map(([x, y], index) => (
                        <rect strokeWidth="0" key={index} width={width + 1} height={height + 1} x={x * width} y={y * height} />
                    ))}
                </svg>
            )}
        </svg>
    );
}

function genRandomPattern(length?: number): number[][] {
    length = length ?? 5;
    return Array.from({ length }, () => [
        Math.floor(Math.random() * 4) + 7, // random x between 7 and 10
        Math.floor(Math.random() * 6) + 1, // random y between 1 and 6
    ]);
}

const APP_FEATURES = [
    {
        title: 'Resume Analysis',
        icon: FileText,
        description: 'AI analyzes your resume to generate relevant, personalized interview questions tailored to your experience.',
    },
    {
        title: 'Voice AI Interviewer',
        icon: Mic,
        description: 'Real-time voice interview with adaptive questioning and a highly natural conversation flow.',
    },
    {
        title: 'Detailed Feedback',
        icon: BarChart,
        description: 'Get comprehensive feedback, scoring, and actionable improvement suggestions after each interview.',
    },
    {
        title: 'Custom Scenarios',
        icon: Settings,
        description: 'Set difficulty levels, select specific question types, and tailor the interview to your target role.',
    },
    {
        title: 'Realistic Mock Environment',
        icon: PlayCircle,
        description: 'Practice under pressure with timed answers and a true-to-life technical or behavioral interview feel.',
    },
    {
        title: 'Track Your Progress',
        icon: Award,
        description: 'Review past performance, study transcript highlights, and watch your skills improve over time.',
    },
];

export function GridFeatureCards() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
            {APP_FEATURES.map((feature, idx) => (
                <FeatureCard
                    key={idx}
                    feature={feature}
                    className="bg-white/5 border-white/10 hover:border-primary/50 transition-all rounded-3xl"
                />
            ))}
        </div>
    );
}

